import { Symbols } from '@ioc/symbols'
import { inject, injectable } from 'inversify'

import { MetricEvent, type MetricsBackendApi, MetricsBackendApiImpl } from '@writefull/core'
import { getDeviceId } from '@writefull/utils'

import { MetricType, MetricsFrontend } from '@domain/metrics'
import { User } from '@domain/user'

import { SessionStore } from '@infrastructure/stores/session/session-store'

const emailDomain = (email: string | undefined) => email?.split('@')[1]

const getAccountType = (user: User | undefined) => {
  if (!user) {
    return 'anonymous'
  }

  if (user.isPremium && user.source == 'overleaf') {
    return 'ai-assist'
  }
  if (user.isPremium && user.isPaid) {
    return 'paid'
  }

  return user.isPremium ? 'premium' : 'freemium'
}

const buildCustomProperties = (
  properties: Record<string, any> | undefined,
  premiumProps:
    | {
        premiumSource: string | undefined
        premiumReason: string | undefined
      }
    | undefined,
): Record<string, any> => {
  return {
    ...properties,
    ...premiumProps,
  }
}

@injectable()
export class TrackingManager {
  constructor(
    @inject(MetricsBackendApiImpl)
    private readonly metricsBackendApi: MetricsBackendApi<MetricsFrontend, MetricType>,
    @inject(SessionStore) private readonly sessionStore: SessionStore,
    @inject(Symbols.ENVIRONMENT) private readonly environment: ImportMetaEnv,
  ) {}

  public async track(
    type: MetricType,
    action: string,
    properties?: Record<string, any> | undefined,
  ): Promise<void> {
    const user = this.sessionStore.getState().user

    const premiumProps = {
      premiumSource: user?.source,
      premiumReason: user?.reason,
    }

    const event: MetricEvent<MetricsFrontend, MetricType> = {
      type,
      action,
      version: '2',
      frontend: 'x',
      deviceId: getDeviceId(),
      uid: user?.uid,
      emailDomain: emailDomain(user?.email),
      accountType: getAccountType(user),
      properties: buildCustomProperties(properties, premiumProps) ?? {},
    }

    if (this.environment.VITE_WF_VERBOSE_METRICS === 'true') {
      // eslint-disable-next-line no-console
      console.log(`track [${event.type}] [${event.action}]`, event)
      return Promise.resolve()
    }

    await this.metricsBackendApi.sendMetric(event)

    return Promise.resolve()
  }
}
